// Central query keys for React Query
// This helps with cache invalidation and organization

export const queryKeys = {
    // Auth
    auth: ['auth'],
    me: () => [...queryKeys.auth, 'me'],

    // Workspaces
    workspaces: ['workspaces'],
    workspace: (id) => [...queryKeys.workspaces, id],
    workspaceTeam: (workspaceId) => [...queryKeys.workspace(workspaceId), 'team'],

    // Projects
    projects: (workspaceId) => ['projects', workspaceId],
    project: (id) => ['project', id],
    projectTeam: (projectId) => [...queryKeys.project(projectId), 'team'],

    // Site Pages
    pages: (projectId) => [...queryKeys.project(projectId), 'pages'],
    page: (projectId, pageId) => [...queryKeys.pages(projectId), pageId],
    components: (projectId) => [...queryKeys.project(projectId), 'components'],
    blocks: (projectId) => [...queryKeys.project(projectId), 'blocks'],

    // Blog
    blogPosts: (projectId, filters) => [...queryKeys.project(projectId), 'blog', 'posts', filters],
    blogPost: (projectId, postId) => [...queryKeys.project(projectId), 'blog', 'posts', postId],
    publishedPost: (projectId, postId) => [...queryKeys.project(projectId), 'blog', 'posts', postId, 'published'],
    blogTags: (projectId, filters) => [...queryKeys.project(projectId), 'blog', 'tags', filters],
    blogTag: (projectId, tagId) => [...queryKeys.project(projectId), 'blog', 'tags', tagId],
    blogAssets: (projectId, filters) => [...queryKeys.project(projectId), 'blog', 'assets', filters],
    blogSetupStatus: (projectId) => [...queryKeys.project(projectId), 'blog', 'setup-status'],
    blogTemplates: () => ['blog', 'templates'],

    // Forms & Contacts
    forms: (projectId) => [...queryKeys.project(projectId), 'forms'],
    form: (projectId, formId) => [...queryKeys.forms(projectId), formId],
    formSubmissions: (projectId, formId) => [...queryKeys.form(projectId, formId), 'submissions'],
    contacts: (projectId) => [...queryKeys.project(projectId), 'contacts'],
    contact: (projectId, contactId) => [...queryKeys.contacts(projectId), contactId],

    // Notifications & Webhooks
    notifications: (projectId) => [...queryKeys.project(projectId), 'notifications'],
    webhooks: (projectId) => [...queryKeys.project(projectId), 'webhooks'],
    webhook: (projectId, webhookId) => [...queryKeys.webhooks(projectId), webhookId],

    
    // Integrations
    integrations: (projectId) => [...queryKeys.project(projectId), 'integrations'],
    integration: (projectId, integrationId) => [...queryKeys.integrations(projectId), integrationId],

    // Domains
    domains: (projectId) => [...queryKeys.project(projectId), 'domains'],
    domain: (projectId, domainId) => [...queryKeys.domains(projectId), domainId],
    domainSearch: (query) => ['domains', 'search', query],

    // Settings
    settings: (projectId) => [...queryKeys.project(projectId), 'settings'],
    seo: (projectId) => [...queryKeys.project(projectId), 'seo'],
    siteSpeed: (projectId) => [...queryKeys.project(projectId), 'site-speed'],

    // Chatbot (workspace-scoped, multi-bot per workspace)
    chatbot: (workspaceId) => [...queryKeys.workspace(workspaceId), 'chatbot'],
    chatbotBots: (workspaceId) => [...queryKeys.chatbot(workspaceId), 'bots'],
    chatbotBot: (workspaceId, botId) => [...queryKeys.chatbotBots(workspaceId), botId],
    chatbotFaqs: (workspaceId, botId) => [...queryKeys.chatbotBot(workspaceId, botId), 'faqs'],
    chatbotSources: (workspaceId, botId) => [...queryKeys.chatbotBot(workspaceId, botId), 'sources'],
    chatbotConversations: (workspaceId, botId, filters = {}) => [...queryKeys.chatbotBot(workspaceId, botId), 'conversations', filters],
    chatbotConversation: (workspaceId, botId, id) => [...queryKeys.chatbotBot(workspaceId, botId), 'conversations', id],

    // Calendar (workspace-scoped)
    calendar: (workspaceId) => [...queryKeys.workspace(workspaceId), 'calendar'],
    calendarEventTypes: (workspaceId) => [...queryKeys.calendar(workspaceId), 'eventTypes'],
    calendarEventType: (workspaceId, id) => [...queryKeys.calendarEventTypes(workspaceId), id],
    calendarBookings: (workspaceId, filters = {}) => [...queryKeys.calendar(workspaceId), 'bookings', filters],
    calendarAnalytics: (workspaceId) => [...queryKeys.calendar(workspaceId), 'analytics'],
    calendarAvailability: (workspaceId) => [...queryKeys.calendar(workspaceId), 'availability'],
    calendarIntegrations: (workspaceId) => [...queryKeys.calendar(workspaceId), 'integrations'],

    // Form Builder (workspace-scoped)
    formBuilder: (workspaceId) => [...queryKeys.workspace(workspaceId), 'formBuilder'],
    formBuilderForms: (workspaceId, filters = {}) => [...queryKeys.formBuilder(workspaceId), 'forms', filters],
    formBuilderForm: (workspaceId, formId) => [...queryKeys.formBuilder(workspaceId), 'forms', formId],
    formBuilderRevisions: (workspaceId, formId) => [...queryKeys.formBuilderForm(workspaceId, formId), 'revisions'],
    formBuilderResponses: (workspaceId, formId, filters = {}) => [...queryKeys.formBuilderForm(workspaceId, formId), 'responses', filters],
    formBuilderResponse: (workspaceId, formId, responseId) => [...queryKeys.formBuilderResponses(workspaceId, formId), responseId],
    formBuilderAnalytics: (workspaceId, formId, days = 30) => [...queryKeys.formBuilderForm(workspaceId, formId), 'analytics', days],
    formBuilderSummary: (workspaceId, formId) => [...queryKeys.formBuilderForm(workspaceId, formId), 'summary'],

    // Cookie Manager (workspace-scoped)
    cookieManager: (workspaceId) => [...queryKeys.workspace(workspaceId), 'cookieManager'],
    cookieBannersRoot: (workspaceId) => [...queryKeys.cookieManager(workspaceId), 'banners'],
    cookieBanners: (workspaceId, filters) => [...queryKeys.cookieBannersRoot(workspaceId), filters],
    cookieBanner: (workspaceId, bannerId) => [...queryKeys.cookieBannersRoot(workspaceId), bannerId],
    cookieBannerPreview: (workspaceId, bannerId) => [...queryKeys.cookieBanner(workspaceId, bannerId), 'preview'],
    cookieMappingsRoot: (workspaceId, bannerId) => [...queryKeys.cookieBanner(workspaceId, bannerId), 'cookies'],
    cookieMappings: (workspaceId, bannerId, filters) => [...queryKeys.cookieMappingsRoot(workspaceId, bannerId), filters],
    cookieScans: (workspaceId, bannerId) => [...queryKeys.cookieBanner(workspaceId, bannerId), 'scans'],
    cookieScan: (workspaceId, bannerId, scanId) => [...queryKeys.cookieScans(workspaceId, bannerId), scanId],
    cookieAnalyticsSummary: (workspaceId, bannerId, days) => [...queryKeys.cookieBanner(workspaceId, bannerId), 'analytics', 'summary', days],
    cookieAnalyticsTimeseries: (workspaceId, bannerId, days) => [...queryKeys.cookieBanner(workspaceId, bannerId), 'analytics', 'timeseries', days],
    cookieConsents: (workspaceId, bannerId, filters) => [...queryKeys.cookieBanner(workspaceId, bannerId), 'consents', filters],
}

// Helper functions for common invalidation patterns
export const invalidateQueries = {
    // Invalidate all workspace data
    workspace: (queryClient, workspaceId) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspaceId) })
        queryClient.invalidateQueries({ queryKey: queryKeys.projects(workspaceId) })
    },

    // Invalidate all project data
    project: (queryClient, projectId) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.project(projectId) })
    },

    // Invalidate specific feature data
    pages: (queryClient, projectId) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.pages(projectId) })
    },

    forms: (queryClient, projectId) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.forms(projectId) })
    },

    team: (queryClient, projectId, workspaceId) => {
        if (projectId) {
            queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) })
        }
        if (workspaceId) {
            queryClient.invalidateQueries({ queryKey: queryKeys.workspaceTeam(workspaceId) })
        }
    },
}
